import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  setConfigToColumns,
  fnPrintByReport,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PUR_RCV_REG_001_POP_001 from "@menu/PUR/SN_PUR_RCV_REG_001/SN_PUR_RCV_REG_001_POP_001";
import { fnCheckAutoProcessV2 } from "../common/sn-pur-util";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import { extractAddInfoFlags } from "@shared/utils/AddInfoUtils";

interface Props {
  Menuprops: menuInfo;
}

// 입고완료(yn_rcv = "Y") 건은 헤더/상세를 수정할 수 없다.
const isReceived = (row: any) =>
  String(row?.yn_rcv ?? "").toUpperCase() === "Y";

// dc_editcon 은 "편집 불가 조건"이며, 평가 전에 행 데이터 값이 문자열 리터럴 안까지
// 치환되므로 컬럼명을 따옴표로 감싸야 한다. (SnColumnsCreate.ts 의 editable 참고)
const RCV_LOCK_CON = "'yn_rcv' == 'Y'";

// 컬럼 전체에 입고완료 편집 차단 조건을 기존 조건과 OR 로 합쳐 넣는다.
const lockColumnsWhenReceived = (cols: any[] = []) =>
  cols.map((col: any) => {
    const prev = (col.dc_editcon ?? "").trim();
    return {
      ...col,
      dc_editcon: prev ? `(${prev}) || (${RCV_LOCK_CON})` : RCV_LOCK_CON,
    };
  });

// 구매입고의뢰등록
const SN_PUR_RCV_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  const addInfoFlagsRef = useRef<string[]>([]);
  const addInfoFlags2Ref = useRef<string[]>([]);

  const [mas8025, setMAS8025] = useState<string>("");

  const loadAddInfoFlags = async (
    idTarget = "grid1",
    flagsRef = addInfoFlagsRef
  ) => {
    try {
      const result: any = await fnGetDataSet({
        id: "sys_addinfo_pop_01_01",
        params: {
          cd_firm: userInfo?.cd_firm ?? "",
          cd_menu: Menuprops?.cd_menu ?? "",
          id_target: idTarget,
        },
      });
      const flags = extractAddInfoFlags(result?.[0] || []);
      flagsRef.current = flags;
      return flags;
    } catch (error: any) {
      flagsRef.current = [];
      return [];
    }
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        const configGrid1 = setConfigToColumns(rtn.grid.grid2, "MAS0002");
        const configGrid2 = setConfigToColumns(configGrid1, "PUR0105");
        const configGrid3 = setConfigToColumns(configGrid2, "MTL0002");
        setColums({
          grid1: lockColumnsWhenReceived(rtn.grid.grid1),
          grid2: lockColumnsWhenReceived(configGrid3),
        });

        setMAS8025(config?.MAS8025?.dc_rmk ?? "");
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {}, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_rcv_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const purRcvHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purRcvHChanges.length > 0) return;

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchInfo.cd_biz,
        cd_biz_rcv: userInfo.cd_biz,
        cd_user_reg: userInfo.cd_user,
        fg_io: "1",
        cd_io: "",
        no_req: "",
        cd_dept: userInfo.cd_dept,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_req: today,
        cd_exch: "000",
        rt_exch: 1,
        fg_money: "1",
        fg_trns: searchInfo.fg_trns ?? "1",
        cd_gp: userInfo.cd_gp,
        st_req: "F",
        yn_autorcv: "N",
        yn_autoprocess: "N",
        cd_cust: searchInfo.cd_cust || "",
        nm_cust_sort: searchInfo.nm_cust || "",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      gridRefs.current["grid1"].current.setFocusedCell(
        focusedNodeH.rowIndex,
        "cd_cust"
      );
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      if (isReceived(focusedNodeH.data)) {
        showDialog("alter", "이미 입고된 건은 행을 추가할 수 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_req: focusedNodeH.data?.no_req,
        no_line: maxNoLine + 1,
        no_line_rcv: 0,
        no_line_rcv_src: 0,
        dt_dlv: today,
        st_req: "F",
      };
      return gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;

    const selectedH =
      gridRefs.current["grid1"].current?.getSelectedRows?.() ?? [];
    if (selectedH.some(isReceived)) {
      await showDialog("alter", "이미 입고된 건은 삭제할 수 없습니다.");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;

    const focusedNodeH = gridRefs.current["grid1"].current?.focusedRowNode();
    if (isReceived(focusedNodeH?.data)) {
      await showDialog("alter", "이미 입고된 건은 행을 삭제할 수 없습니다.");
      return;
    }

    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    gridRefs.current["grid2"].current?.deleteSelectedRows();

    //setFooterDataToRow();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return false;

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const purRcvReqHChanges = gridRefs.current["grid1"].current.getChange();
      const purRcvReqLChanges = gridRefs.current["grid2"].current.getChange();

      const receivedChanges = [
        ...(purRcvReqHChanges.update ?? []),
        ...(purRcvReqHChanges.delete ?? []),
        ...(purRcvReqLChanges.insert ?? []),
        ...(purRcvReqLChanges.update ?? []),
        ...(purRcvReqLChanges.delete ?? []),
      ].filter(isReceived);
      if (receivedChanges.length > 0) {
        await showDialog("alter", "이미 입고된 건은 수정할 수 없습니다.");
        return false;
      }
      // if (!validateQty(purRcvReqLChanges.insert)) {
      //   return false;
      // }
      const headerInserts = purRcvReqHChanges.insert ?? [];
      const lineInserts = purRcvReqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }

      if (
        purRcvReqHChanges.modifiedRowcount === 0 &&
        purRcvReqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_req",
        cd_module: Menuprops.cd_module,
        no_line: 4,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [purRcvReqHChanges, purRcvReqLChanges],
        cd_menu: Menuprops.cd_menu,
      });
      //gridRefs.current["grid2"].current.applyFilters({ no_req: noSlip });

      const saveData = {
        pur_rcvreqh: purRcvReqHChanges,
        pur_rcvreql: purRcvReqLChanges,
      };
      const ok = await fnSave("SN_PUR_RCV_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid2"].current.forEachNode((node: RowNode) => {
          console.log(node.data.no_req, node.data.no_line);
        });
        return false;
      }

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const validateQty = (rows: any[]): boolean => {
    for (const row of rows) {
      const qt = Number(row.qt ?? 0);
      const qtApp = Number(row.qt_app ?? 0);

      if (qt > qtApp) {
        showDialog("alter", "의뢰량이 발주량을 초과하였습니다.");
        return false;
      }
    }
    return true;
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const rowChanges = gridRefs.current["grid1"].current.getChange().insert;
      if (rowChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      gridRefs.current["grid1"].current.stopEditing();
      const selectedFormData = searchRef.current.getFormdata();
      let selectedData;
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH) {
        const rowData = focusedNodeH.data;
        selectedData = {
          cd_biz: rowData.cd_biz,
          cd_cust: rowData.cd_cust,
          nm_cust: rowData.nm_cust_sort,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
          fg_trns: rowData.fg_trns,
        };
      }

      const component = (
        <SN_PUR_RCV_REG_001_POP_001 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "발주적용",
        children: component,
        width: "72%",
        validation: true,
      });
      if (!result || result === "close") return;

      const today = dateToString(new Date());
      const headerResult = result[0];
      const lineResult: any[] = result[1];
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        // for (const key in headerResult[0]) {
        //   focusedRowNodeH.setDataValue(key, headerResult[0][key]);
        // }
        focusedRowNodeH.setDataValue("cd_biz", headerResult[0].cd_biz);
        focusedRowNodeH.setDataValue("cd_biz_isu", headerResult[0].cd_biz);
        focusedRowNodeH.setDataValue("dt_req", today);
        focusedRowNodeH.setDataValue("cd_cust", headerResult[0].cd_cust);
        focusedRowNodeH.setDataValue("nm_cust", headerResult[0].nm_cust);
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort
        );
        focusedRowNodeH.setDataValue("fg_trns", headerResult[0].fg_trns);
        focusedRowNodeH.setDataValue("fg_money", headerResult[0].fg_money);
        focusedRowNodeH.setDataValue("cd_exch", headerResult[0].cd_exch);
        focusedRowNodeH.setDataValue("rt_exch", headerResult[0].rt_exch);
        focusedRowNodeH.setDataValue("cd_io", headerResult[0].cd_io);
        focusedRowNodeH.setDataValue("am", headerResult[0].am);
        focusedRowNodeH.setDataValue("am_k", headerResult[0].am_k);
        focusedRowNodeH.setDataValue("am_vat", headerResult[0].am_vat);
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("no_po", result.no_po);
            addedNode.setDataValue("no_line_po", result.no_line);
            addedNode.setDataValue("rt_exch", result.rt_exch);
            addedNode.setDataValue("cd_item", result.cd_item);
            addedNode.setDataValue("nm_item", result.nm_item);
            addedNode.setDataValue("dc_item_spec", result.dc_item_spec);
            addedNode.setDataValue("dc_item_unit", result.dc_item_unit);
            addedNode.setDataValue("dt_dlv", result.dt_dlv);
            addedNode.setDataValue("cd_wh", result.cd_wh);
            addedNode.setDataValue("nm_wh", result.nm_wh);
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
            addedNode.setDataValue("no_cbs", result.no_cbs, "noEvent");
            addedNode.setDataValue("qt_app", result.qt_app);
            // 입고수량은 입고처리 트리거로만 채워지는 값이므로 발주적용 시 0으로 시작한다
            addedNode.setDataValue("qt_rcv", 0);
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("um_k", result.um_k);
            addedNode.setDataValue("am", result.am);
            addedNode.setDataValue("am_k", result.am_k);
            addedNode.setDataValue("am_vat", result.am_vat);
            addedNode.setDataValue("am_supply", result.am_supply);
            addedNode.setDataValue("um_supply", result.um_supply);
            addedNode.setDataValue("fg_vat", result.fg_vat);
            addedNode.setDataValue("dc_rmk", result.dc_rmk);
            addedNode.setDataValue("dc_unit_po", result.dc_unit_po);
            addedNode.setDataValue("qt_unit_po", result.qt_unit_po);
            addedNode.setDataValue("qt_po", result.qt_app_po);
            addedNode.setDataValue("qt_app_po", result.qt_app_po);
            addedNode.setDataValue("um_po", result.um_po);
            addedNode.setDataValue("yn_qc", result.yn_iqc);
            if (result.yn_iqc === "Y") {
              addedNode.setDataValue("st_qc", "P");
            }
            addedNode.setDataValue("cd_pjt", result.cd_pjt);
            addedNode.setDataValue("nm_pjt", result.nm_pjt);
            addedNode.setDataValue("yn_lot", result.yn_lot);
            addedNode.setDataValue("qt_lotsize", result.qt_lotsize);
          }
        }
      });
    }
  };

  // 부가항목설정 팝업 호출
  // 부가항목 설정 팝업을 열고 저장 시 화면 메타/데이터를 재적용한다.
  const onOpenAddInfoPopup = async (idTarget = "grid1") => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos =
      idTarget === "grid2" ? columns.grid2 || [] : columns.grid1 || [];
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos,
    });
    if (!saved) return;

    // 한글 주석: 화면 메타(getInitFormData) 결과를 그리드 컬럼 설정으로 변환한다.
    const buildGridColumns = (rtn: any) => {
      const configGrid1 = setConfigToColumns(rtn.grid.grid2, "MAS0002");
      const configGrid2 = setConfigToColumns(configGrid1, "PUR0105");
      const configGrid3 = setConfigToColumns(configGrid2, "MTL0002");
      return {
        grid1: rtn.grid.grid1,
        grid2: configGrid3,
      };
    };

    const rtn: any = await getInitFormData();
    setFormControlData(rtn.formControlData);
    setFormData((prev: any) => ({
      ...(rtn.formData || {}),
      ...(prev || {}),
    }));
    setColums(buildGridColumns(rtn));

    // 한글 주석: 항목설정 변경 후 부가항목 키 목록을 다시 로드한다.
    // ref에 즉시 반영되므로 바로 아래 onSearch가 갱신된 목록으로 조회한다.
    idTarget === "grid2"
      ? await loadAddInfoFlags(idTarget, addInfoFlags2Ref)
      : await loadAddInfoFlags(idTarget, addInfoFlagsRef);

    await onSearch();
  };

  // grid1 헤더 우클릭 추가 메뉴를 처리한다.
  const onGrid1ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    if (event.id === "extra_item_setting") {
      await onOpenAddInfoPopup("grid1");
    }
  };

  // grid2 헤더 우클릭 추가 메뉴를 처리한다.
  const onGrid2ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    if (event.id === "extra_item_setting") {
      await onOpenAddInfoPopup("grid2");
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_req = selectedRow.no_req;

      setSelectedDocu(no_req);
    } else {
      return;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    switch (id) {
      case "deleteRow":
        onDeleteRow();
        break;
      case "print":
        const checkedRows = gridRefs.current["grid2"].current.getCheckedRows();
        if (checkedRows.length < 1) {
          await showDialog("alter", "선택된 항목이 없습니다.");
          return;
        }
        const hasInvalid = checkedRows.some(
          (row: any) => row.yn_qc === "Y" && row.yn_confirm !== "Y"
        );
        if (hasInvalid) {
          await showDialog("alter", "검사가 완료되지 않은 건이 있습니다.");
          return;
        }
        const noReq = checkedRows[0].no_req;
        const noLines = checkedRows.map((row: any) => row.no_line).join(",");
        const printParams = {
          cd_firm: userInfo.cd_firm,
          no_req: noReq,
          no_line: noLines,
        };
        await fnPrintByReport("rpt_pur_rcv_reg_001_chem_01", printParams);
        gridRefs.current["grid2"].current.clearCheckBox();
        break;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    // const rowNode = event.rowNode;
    if (colId === "rt_exch") {
      const rt_exch = Number(rowData.rt_exch ?? 1);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (node) {
          node.setDataValue("rt_exch", rt_exch);
        }
      });
    }
    if (colId === "no_req") {
      gridRefs.current["grid2"].current.applyFilters({
        no_req: rowData.no_req,
      });
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "nm_wh") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 1) return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data.cd_wh === "" ||
            node.data.cd_wh === null ||
            node.data.cd_wh === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      if (
        rowData.cd_wh === "" ||
        rowData.cd_wh === null ||
        rowData.cd_wh === undefined
      )
        return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );
      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          }
        );
      } else {
        return;
      }
    }

    switch (colId) {
      case "um_k": {
        // 원화단가 수정 시 단가 역산 (단가 = 원화단가 / 환율) 후 금액 재계산
        const rt_exch = Number(
          rowData.rt_exch ||
            gridRefs.current["grid1"].current?.focusedRowNode()?.data
              ?.rt_exch ||
            1
        );
        const um_k = Number(rowData.um_k ?? 0);
        rowNode.setDataValue(
          "um",
          rt_exch === 0 ? 0 : um_k / rt_exch,
          "noEvent"
        );
        // 서버 CalAm 은 um_k 기준 계산을 하지 않으므로, 역산한 단가 기준(um)으로 단가 수정과 동일하게 계산
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "um");
        break;
      }
      case "qt":
      case "um":
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
        break;
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "구매의뢰정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
            header={
              <div>
                <SnButton
                  text={"발주적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              rowSelection: "multiple",
              title: "상세정보",
              ischeckboxSelection: mas8025 === "EICT",
            }}
            events={grids.grid2.events}
            header={
              <div>
                <SnButton
                  text={"출력"}
                  visible={mas8025 === "EICT"}
                  onClick={() => {
                    onGrid2BtnsClick("print");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_PUR_RCV_REG_001;
